function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
// @ts-strict-ignore
import React, { useState } from 'react';
import { motion } from 'motion/react';
import partition from 'lodash/partition';
import produce from 'immer';
import camelCase from 'lodash/camelCase';
import { EntryPart, EntrySectionUpdates, FiltersOperator, MetricEntryBehavior, QueryFilterEntry, RowLabelType, SelectorTypeDefinitions } from 'common/query-builder/query-entry-section/types';
import { Event, EventItem } from 'common/query-builder/query-block/events-menu/types';
import { MetricType } from 'common/types/reports/bookmark';
import { QueryBlockReact } from 'common/query-builder/query-block';
import { MenuType } from 'common/query-builder/query-block/types';
import { ButtonTheme } from 'common/components/button/constants';
import { SelectorType as QueryBlockSelectorType } from 'common/query-builder/query-block/constants';
import { OverflowControls } from 'common/query-builder/query-entry-section/row-types/new-metric-entry/common/controls';
import { ControlBarAction, ControlBarActionItem, ControlBarActionItems } from 'common/query-builder/query-entry-section/constants';
import { ButtonReact } from 'common/components/button';
import { createFirstTimeEventFilter, isFirstTimeEventFilter } from 'common/widgets/property-filter-menu/models/utils';
// eslint-disable-next-line import/no-cycle
import { QueryEntrySectionReact } from 'common/query-builder/query-entry-section';
import { getFilterEntriesFromFilters } from 'iron/common/query-builder/query-entry-section/row-types/filter/utils';
import { PropertyFilter } from 'common/widgets/property-filter-menu/models/property-filter';
import { getEventNamesFromEntries } from 'iron/common/query-builder/query-entry-section/row-types/row-util';
import { RowFiltersPrefix } from 'common/query-builder/query-entry-section/components/filters';
import { isXauMathType } from 'common/query-builder/query-builder/util';
import { BEHAVIOR_PROPERTIES } from 'common/query-builder/query-block/properties-menu/constants';
import { SvgIconReact } from 'common/components/svg-icon';
import { userCookieUtils } from 'common/report/util/user-identity-from-cookie';
import type { RowProps } from 'iron/common/query-builder/query-entry-section/row-types/new-metric-entry/types/catalog';
import { useFeatureFlag } from 'common/hooks/useFeatureFlag';
import { ProjectFeatureFlag } from 'common/report/constants';
import { SVG_ICONS } from 'common/components/svg-icon/icons';
import styles from './index.module.styl';
type EventRowProps = RowProps & {
  additionalOverflowControls?: ControlBarActionItem[];
  behavior: MetricEntryBehavior;
  /** overrides the selected event name */
  label?: string | null;
  onClose?: (() => void) | null | undefined;
  onDelete?: (() => void) | null | undefined;
  onDuplicate?: (() => void) | null | undefined;
  onSelect?: ((behavior: MetricEntryBehavior) => void) | null | undefined;
  /** renders a label before the event name */
  prefixLabel?: string | null;
  /**
   * Open the query block for event selection.
   */
  openQueryBlock?: boolean;
  /**
   * Show this icon for the row icon.
   */
  rowIcon?: keyof typeof SVG_ICONS | null;
  /** renders a label after the event name */
  suffixLabel?: string | null;
};

/**
 * Provides a component for rendering a behavior event selection together with filters.
 *
 * @todo(metrics) filters are not matching designs
 */
export function BehaviorEventRow(props: EventRowProps) {
  var _userCookieUtils$getU, _props$rowIcon, _props$openQueryBlock;
  const [entryEl, setEntryEl] = useState<HTMLDivElement>(null);
  const [isStagingFilter, setIsStagingFilter] = useState(false);
  const hasMetricLevelDataGroups = useFeatureFlag(ProjectFeatureFlag.MetricLevelDataGroups);
  const userId = (_userCookieUtils$getU = userCookieUtils.getUserIdentityFromCookie()) === null || _userCookieUtils$getU === void 0 ? void 0 : _userCookieUtils$getU.id;
  const hasFirstTimeFilter = props.behavior.filters.some(isFirstTimeEventFilter);
  const [firstTimeFilters, regularFilters] = partition(props.behavior.filters, isFirstTimeEventFilter);
  const filtersOperator = props.behavior.filtersDeterminer === "all" ? FiltersOperator.And : FiltersOperator.Or;
  const filterParts = {
    [EntryPart.Filters]: getFilterEntriesFromFilters(regularFilters, filtersOperator),
    [EntryPart.FirstTimeFilters]: getFilterEntriesFromFilters(firstTimeFilters, filtersOperator)
  };
  const handleAddFilter = () => setIsStagingFilter(true);
  const handleDelete = () => props.onDelete();
  const handleDuplicate = () => props.onDuplicate();
  const handleEventSelected = (selectedEvent: EventItem) => {
    const result = produce(props.behavior, behavior => {
      var _selectedEvent$id;
      behavior.name = selectedEvent.value;
      behavior.id = (_selectedEvent$id = selectedEvent.id) !== null && _selectedEvent$id !== void 0 ? _selectedEvent$id : null;
      behavior.type = selectedEvent.custom ? MetricType.CustomEvent : MetricType.Event;
    });
    setIsStagingFilter(false);
    props.onSelect(result);
  };
  const handleFiltersUpdate = function handleFiltersUpdate(filters: PropertyFilter[]): void {
    let updatedFiltersOperator: FiltersOperator | null = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
    const result = produce(props.behavior, behavior => {
      behavior.filters = filters;
      behavior.filtersDeterminer = (updatedFiltersOperator || filtersOperator) === FiltersOperator.And ? "all" : "any";
    });
    setIsStagingFilter(false);
    props.onSelect(result);
  };
  const handleAddFirstTimeFilter = () => !hasFirstTimeFilter && handleFiltersUpdate([createFirstTimeEventFilter(), ...props.behavior.filters]);

  /**
   * Event filters are separated into regular and first time filters, since first time filters
   * have special constraints like only allowing one instance of it inside filters and always
   * being at the bottom of all other filters
   */
  const generateFilters = (source: EntryPart.Filters | EntryPart.FirstTimeFilters) => {
    var _props$entry$measurem;
    /** renders the AND/OR filter operator selector */
    const conjunctionRenderer = _ref => {
      let {
        index: filterIndex,
        entry: filterEntry
      } = _ref;
      return filterIndex === 0 && filterParts[source].length > 1 && <RowFiltersPrefix filterIndex={filterIndex} filter={new PropertyFilter((filterEntry as QueryFilterEntry).event)} filtersOperator={props.behavior.filtersDeterminer === "all" ? FiltersOperator.And : FiltersOperator.Or} isReadOnlyMode={false} forNewUI handleChange={item => handleFiltersUpdate(props.behavior.filters, item.value as FiltersOperator)} />;
    };
    return <QueryEntrySectionReact containedSection indentFilters elref={camelCase("entry-".concat(source))} autoactivatePlaceholders enableAllDataGroupsForScdProps={props.sectionProps.enableAllDataGroupsForScdProps} eventNames={getEventNamesFromEntries(props.entry)} chartType={props.currentChartType} supportB2bCohorts={props.sectionProps.dataGroupsEnabled} dataGroupsEnabled={props.sectionProps.dataGroupsEnabled} projectId={props.sectionProps.projectId} workspaceId={props.sectionProps.workspaceId} dataGroupId={hasMetricLevelDataGroups ? (_props$entry$measurem = props.entry.measurement.dataGroupId) !== null && _props$entry$measurem !== void 0 ? _props$entry$measurem : props.entry.behavior.dataGroupId : props.sectionProps.dataGroupId} disableAddEntry rowTypes={[SelectorTypeDefinitions.Filter]} rowLabelType={RowLabelType.None} hideEvents={false} projectHasCohorts projectHasPeople disabledItems={isXauMathType(props.entry.measurement.aggregationOperator) ? Array.from(BEHAVIOR_PROPERTIES.keys()).reduce((acc, key) => {
      acc[key] = true;
      return acc;
    }, {}) : {}} rowDraggable={false} trackProperties={{
      sectionName: "inline-filter"
    }} disableCloneEntry={source === EntryPart.FirstTimeFilters} entries={filterParts[source]} sectionName={"inline-filter"} renderSuffix={conjunctionRenderer} onUpdate={(updates: EntrySectionUpdates<QueryFilterEntry>) => {
      const updatedFilters = Object.values(_objectSpread(_objectSpread({}, filterParts), {}, {
        [source]: updates.entries
      })).flat().map(entry => entry.event as PropertyFilter);
      handleFiltersUpdate(updatedFilters);
    }} stagingRow={source === EntryPart.Filters && isStagingFilter} onAbortStage={() => setIsStagingFilter(false)} data-sentry-element="QueryEntrySectionReact" data-sentry-component="generateFilters" data-sentry-source-file="index.tsx" />;
  };
  return <motion.div ref={setEntryEl} className={styles.eventRow} whileHover='hovered' variants={{
    hovered: {
      opacity: 1
    }
  }} data-sentry-element="unknown" data-sentry-component="BehaviorEventRow" data-sentry-source-file="index.tsx">
      <motion.div className={styles.header} variants={{
      hovered: {
        paddingLeft: "4px"
      }
    }} transition={{
      type: "spring",
      visualDuration: 0.1,
      bounce: 0.3
    }} data-sentry-element="unknown" data-sentry-source-file="index.tsx">
        <div className={styles.eventIndex}>
          <SvgIconReact className={styles.icon} icon={(_props$rowIcon = props.rowIcon) !== null && _props$rowIcon !== void 0 ? _props$rowIcon : "behavior-event"} data-sentry-element="SvgIconReact" data-sentry-source-file="index.tsx" />
          {props.label}
        </div>

        {/* renders the actual event selection menu */}
        <QueryBlockReact className={styles.content} type={MenuType.Events} showSessionEvent selectorIcon='' selectorType={QueryBlockSelectorType.Button} theme={ButtonTheme.QueryBuilder} label={props.label} userId={userId}
      // disabled={props.sectionProps.disabled}
      disabled={false}
      // workspaceId={props.sectionProps.workspaceId}
      prefixLabel={props.prefixLabel} suffixLabel={props.suffixLabel} contextPanelParentElement={entryEl} selected={{
        label: null,
        id: props.behavior.id,
        value: props.behavior.name,
        custom: props.behavior.type === MetricType.CustomEvent
      } satisfies Event} onClose={() => {
        var _props$onClose;
        return (_props$onClose = props.onClose) === null || _props$onClose === void 0 ? void 0 : _props$onClose.call(props);
      }} onSelect={detail => handleEventSelected(detail.item)} open={(_props$openQueryBlock = props.openQueryBlock) !== null && _props$openQueryBlock !== void 0 ? _props$openQueryBlock : false} data-sentry-element="QueryBlockReact" data-sentry-source-file="index.tsx" />

        {/* overflow controls for each individual event in a behavior */}
        <div className={styles.actions}>
          <ButtonReact theme={ButtonTheme.Tertiary} icon='filter-xs' onClick={handleAddFilter} data-sentry-element="ButtonReact" data-sentry-source-file="index.tsx" />
          {props.onDelete && <ButtonReact theme={ButtonTheme.Tertiary} icon='x' onClick={handleDelete} />}
          <OverflowControls items={[{
          items: [_objectSpread(_objectSpread({}, ControlBarActionItems[ControlBarAction.Filter]), {}, {
            onSelect: handleAddFilter
          }), /** events can only have one first time filter */
          !hasFirstTimeFilter && _objectSpread(_objectSpread({}, ControlBarActionItems[ControlBarAction.FirstTimeFilter]), {}, {
            onSelect: handleAddFirstTimeFilter
          }), props.onDelete && _objectSpread(_objectSpread({}, ControlBarActionItems[ControlBarAction.Delete]), {}, {
            onSelect: handleDelete
          }), _objectSpread(_objectSpread({}, ControlBarActionItems[ControlBarAction.Duplicate]), {}, {
            onSelect: handleDuplicate
          }), ...(props.additionalOverflowControls || [])].filter(Boolean)
        }]} data-sentry-element="OverflowControls" data-sentry-source-file="index.tsx" />
        </div>
      </motion.div>

      {/* renders filters for the behavior event */}
      <div className={styles.filters}>
        {generateFilters(EntryPart.Filters)}
        {generateFilters(EntryPart.FirstTimeFilters)}
      </div>
    </motion.div>;
}